import { apiClient } from '@/services/api';

// Initialize savedJobs from localStorage when store is created
let initialSavedJobs = [];
try {
  // Only load from localStorage if the user is logged in
  const isLoggedIn = !!localStorage.getItem('token');
  
  if (isLoggedIn) {
    const savedJobsStr = localStorage.getItem('savedJobs');
    if (savedJobsStr) {
      const parsed = JSON.parse(savedJobsStr);
      if (Array.isArray(parsed)) {
        initialSavedJobs = parsed;
      }
    }
  } else {
    // Clear saved jobs from localStorage if not logged in
    localStorage.removeItem('savedJobs');
  }
} catch (error) {
  console.error('Error initializing saved jobs from localStorage:', error);
}

const state = {
  savedJobs: initialSavedJobs,
  loading: false,
  error: null
};

const mutations = {
  SET_SAVED_JOBS(state, jobs) {
    state.savedJobs = Array.isArray(jobs) ? jobs : [];
  },
  ADD_SAVED_JOB(state, job) {
    // Ensure savedJobs is an array
    if (!Array.isArray(state.savedJobs)) {
      state.savedJobs = [];
    }
    
    
    // Check if this job already exists in the saved jobs
    // We need to check multiple possible identifiers because jobs can be saved in different formats
    
    // Check direct ID match
    const existsByDirectId = state.savedJobs.some(savedJob => savedJob.id === job.id);
    
    // Check job ID match via job.job property
    const existsByJobProperty = state.savedJobs.some(savedJob => 
      savedJob.job === job.job ||
      savedJob.job === job.id ||
      (job.job && savedJob.job === job.job)
    );
    
    // Check job details ID match
    const existsByJobDetails = state.savedJobs.some(savedJob => 
      (savedJob.job_details && job.job_details && savedJob.job_details.id === job.job_details.id) ||
      (savedJob.job_details && savedJob.job_details.id === job.id) ||
      (job.job_details && job.job_details.id && savedJob.job === job.job_details.id)
    );
    
    // Check if any of the existence checks passed
    const exists = existsByDirectId || existsByJobProperty || existsByJobDetails;
    
    if (!exists) {
      state.savedJobs.push(job);
    }
  },
  REMOVE_SAVED_JOB(state, jobId) {
    // Ensure savedJobs is an array
    if (!Array.isArray(state.savedJobs)) {
      state.savedJobs = [];
      return;
    }
    
    
    // First try to remove by direct ID match
    let filteredJobs = state.savedJobs.filter(job => job.id !== jobId);
    
    // If no jobs were removed, try to remove by job_details.id match
    if (filteredJobs.length === state.savedJobs.length) {
      filteredJobs = state.savedJobs.filter(job => 
        !(job.job_details && job.job_details.id === jobId)
      );
    }
    
    // If still no jobs were removed, try to remove by job_id match
    if (filteredJobs.length === state.savedJobs.length) {
      filteredJobs = state.savedJobs.filter(job => job.job_id !== jobId);
    }
    
    state.savedJobs = filteredJobs;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  }
};

const actions = {
  // Fetch saved jobs from API and localStorage
  async fetchSavedJobs({ commit }) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    
    // First check if user is logged in
    const isLoggedIn = !!localStorage.getItem('token');
    if (!isLoggedIn) {
      // If not logged in, clear saved jobs and return
      commit('SET_SAVED_JOBS', []);
      commit('SET_LOADING', false);
      return;
    }
    
    try {
      // First try to get from API if user is authenticated
      try {
        const response = await apiClient.get('/saved-jobs/');
        
        // Make sure we're working with an array
        let savedJobs = [];
        if (Array.isArray(response.data)) {
          savedJobs = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          savedJobs = response.data.results;
        } else {
          savedJobs = [];
        }
        
        
        // Add identifiers for easy lookup
        savedJobs = savedJobs.map(job => {
          if (job.job_details) {
            // Make sure we can find these jobs by their job_details.id
            return {
              ...job,
              job_id: job.job_details.id
            };
          }
          return job;
        });
        
        commit('SET_SAVED_JOBS', savedJobs);
        // Update localStorage with the latest data from API
        localStorage.setItem('savedJobs', JSON.stringify(savedJobs));
      } catch (apiError) {
        console.error('API Error fetching saved jobs:', apiError);
        if (apiError.response) {
          console.error('API Error details:', apiError.response.data);
        }
        
        // Fallback to localStorage if API fails
        const savedJobsData = localStorage.getItem('savedJobs');
        if (savedJobsData) {
          try {
            const parsedData = JSON.parse(savedJobsData);
            if (Array.isArray(parsedData)) {
              commit('SET_SAVED_JOBS', parsedData);
            } else {
              console.error('Saved jobs data is not an array, resetting to empty array');
              commit('SET_SAVED_JOBS', []);
              // Clean up corrupted data
              localStorage.setItem('savedJobs', JSON.stringify([]));
            }
          } catch (parseError) {
            console.error('Error parsing saved jobs data:', parseError);
            commit('SET_SAVED_JOBS', []);
            // Clean up corrupted data
            localStorage.setItem('savedJobs', JSON.stringify([]));
          }
        } else {
          commit('SET_SAVED_JOBS', []);
        }
      }
    } catch (error) {
      console.error('Error fetching saved jobs:', error);
      commit('SET_ERROR', 'Failed to load saved jobs. Please try again.');
      commit('SET_SAVED_JOBS', []);
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  // Save job to API and localStorage
  async saveJob({ commit, state }, job) {
    // First check if user is logged in
    const isLoggedIn = !!localStorage.getItem('token');
    if (!isLoggedIn) {
      commit('SET_ERROR', 'Authentication required');
      return false;
    }
    
    try {
      let savedJobData = null;
      
      // Try to save to API first
      try {
        const response = await apiClient.post('/saved-jobs/', { job_id: job.id });
        
        // Use the response data from the API, which should contain the saved job object
        savedJobData = response.data;
        
        // If response doesn't contain job_details but we have the job object,
        // combine them for better display in the UI
        if (savedJobData && !savedJobData.job_details && job) {
          savedJobData = {
            ...savedJobData,
            job_details: job,
            job_id: job.id // Add job_id for easier lookup
          };
        }
      } catch (apiError) {
        console.error('API Error when saving job:', apiError);
        if (apiError.response) {
          console.error('API Error details:', apiError.response.data);
          
          // If job is already saved, we can continue
          if (!(apiError.response.status === 200 &&
              apiError.response.data &&
              apiError.response.data.detail === "Job already saved")) {
            throw apiError;
          }
        } else {
          throw apiError;
        }
      }
      
      // If we have saved job data from the API, use it
      if (savedJobData) {
        commit('ADD_SAVED_JOB', savedJobData);
      } else {
        // Otherwise use the job object that was passed in
        
        // Create a saved job object similar to what would be returned by the API
        const localSavedJob = {
          id: `local_${Date.now()}`, // Temporary ID until we fetch from API
          job: job.id,
          job_details: job,
          job_id: job.id,
          date_saved: new Date().toISOString()
        };
        
        commit('ADD_SAVED_JOB', localSavedJob);
      }
      
      // Save to localStorage as backup
      if (Array.isArray(state.savedJobs)) {
        localStorage.setItem('savedJobs', JSON.stringify(state.savedJobs));
      } else {
        // If somehow savedJobs is not an array, initialize it
        commit('SET_SAVED_JOBS', savedJobData ? [savedJobData] : [job]);
        localStorage.setItem('savedJobs', JSON.stringify(savedJobData ? [savedJobData] : [job]));
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error saving job:', error);
      commit('SET_ERROR', 'Failed to save job. Please try again.');
      return { success: false, error };
    }
  },
  
  // Remove job from saved jobs
  async removeJob({ commit, state }, jobId) {
    // First check if user is logged in
    const isLoggedIn = !!localStorage.getItem('token');
    if (!isLoggedIn) {
      commit('SET_ERROR', 'Authentication required');
      return { success: false };
    }
    
    try {
      // First figure out the actual saved job ID
      let actualJobId = jobId;
      
      // If the job is saved with a different ID (e.g. the saved job record ID versus the job ID)
      // we need to find the correct saved job record to delete
      if (Array.isArray(state.savedJobs)) {
        // Look for matching job_details.id
        const jobWithMatchingDetails = state.savedJobs.find(
          savedJob => savedJob.job_details && savedJob.job_details.id === jobId
        );
        
        if (jobWithMatchingDetails) {
          actualJobId = jobWithMatchingDetails.id;
        } else {
          // Look for matching job ID in the job field
          const jobWithMatchingJob = state.savedJobs.find(
            savedJob => savedJob.job === jobId || savedJob.job_id === jobId
          );
          
          if (jobWithMatchingJob) {
            actualJobId = jobWithMatchingJob.id;
          }
        }
      }
      
      
      // Try to remove from API first
      try {
        await apiClient.delete(`/saved-jobs/${actualJobId}/`);
      } catch (apiError) {
        console.error('API Error when removing job:', apiError);
        // If 404, it's already removed from the server, so we can just remove it locally
        if (apiError.response && apiError.response.status !== 404) {
          throw apiError;
        }
      }
      
      // Remove from local state
      commit('REMOVE_SAVED_JOB', jobId);
      
      // Update localStorage
      localStorage.setItem('savedJobs', JSON.stringify(state.savedJobs));
      
      return { success: true };
    } catch (error) {
      console.error('Error removing job:', error);
      commit('SET_ERROR', 'Failed to remove job. Please try again.');
      return { success: false, error };
    }
  },
  
  // Check if a job is saved
  isJobSaved({ state }, jobId) {
    // Convert jobId to number if it's a string
    const id = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId;
    
    
    if (!Array.isArray(state.savedJobs)) {
      return false;
    }
    
    // First check direct job ID match
    const directMatch = state.savedJobs.some(job => job.id === id);
    if (directMatch) {
      return true;
    }
    
    // Then check if it's in job_details nested structure
    const nestedMatch = state.savedJobs.some(job => 
      job.job_details && job.job_details.id === id
    );
    
    return nestedMatch;
  },
  
  // Toggle job saved state
  async toggleSaveJob({ dispatch, getters }, job) {
    if (!job || !job.id) {
      console.error('Invalid job object provided to toggleSaveJob');
      return { success: false };
    }
    
    // Check if already saved
    const isAlreadySaved = getters.isJobSaved(job.id);
    
    if (isAlreadySaved) {
      // Remove from saved jobs
      return await dispatch('removeJob', job.id);
    } else {
      // Add to saved jobs
      const result = await dispatch('saveJob', job);
      return result;
    }
  }
};

const getters = {
  savedJobs: state => Array.isArray(state.savedJobs) ? state.savedJobs : [],
  savedJobIds: state => {
    if (!Array.isArray(state.savedJobs)) return [];
    
    // Collect direct IDs and nested IDs
    const ids = [];
    state.savedJobs.forEach(job => {
      if (job.id) ids.push(job.id);
      // Check for nested job_details
      if (job.job_details && job.job_details.id) {
        ids.push(job.job_details.id); 
      }
      // If we have a job_id instead (from API)
      if (job.job_id) ids.push(job.job_id);
    });
    
    return ids;
  },
  isJobSaved: state => jobId => {
    // Convert jobId to number if it's a string
    const id = typeof jobId === 'string' ? parseInt(jobId, 10) : jobId;
    
    if (!Array.isArray(state.savedJobs)) {
      return false;
    }
    
    // First check direct job ID match
    const directMatch = state.savedJobs.some(job => job.id === id);
    if (directMatch) {
      return true;
    }
    
    // Then check if it's in job_details nested structure
    const nestedMatch = state.savedJobs.some(job => 
      job.job_details && job.job_details.id === id
    );
    
    // Check for job_id property that might be used by API
    const apiMatch = state.savedJobs.some(job => job.job_id === id);
    
    return directMatch || nestedMatch || apiMatch;
  },
  loading: state => state.loading,
  error: state => state.error
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}; 